import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion, type Variants } from 'framer-motion';
import { useIsMobile } from '../hooks/useIsMobile';
import { ChevronDownIcon } from 'lucide-react';
import { services } from '../data/services';
import { focusInView } from '../utils/reveal';
import { QuestShard } from './QuestShard';
import { useQuest } from '../hooks/useQuest';
import { quest } from '../utils/quest';
const EASE = [0.23, 1, 0.32, 1] as const;
const SOFT = [0.33, 1, 0.68, 1] as const;

/** Phones: one trigger for the whole list, titles cascading in */
const GROUP: Variants = {
  hidden: {},
  shown: {
    transition: {
      delayChildren: 0.05,
      staggerChildren: 0.07
    }
  }
};
const ITEM: Variants = {
  hidden: {
    opacity: 0,
    y: 14,
    filter: 'blur(10px)'
  },
  shown: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transitionEnd: {
      filter: 'none'
    },
    transition: {
      opacity: {
        duration: 0.8,
        ease: SOFT
      },
      filter: {
        duration: 1,
        ease: SOFT
      },
      y: {
        duration: 1.1,
        ease: SOFT
      }
    }
  }
};

/**
 * Services list in the style of the reference: bold titles with a thin chevron; clicking one opens its
 * paragraph underneath (one open at a time). Text uses [data-ink] so it flips white on the dark backdrop.
 */
export function ServicesAccordion() {
  const reduce = useReducedMotion();
  const [open, setOpen] = useState<number | null>(null);
  const mobile = useIsMobile();
  const questState = useQuest();
  const questStep = questState.status === 'active' ? questState.step : null;
  const toggle = (i: number) => setOpen(open === i ? null : i);
  return (
    // Fixed-width block (parent pins it to the right edge): titles share a left edge, opening a panel never shifts them
    // Phones: full width on the left, so open paragraphs use the whole line
    // Phones: the list reveals as one group — a single trigger once it's a little way on screen, then the
    // titles cascade in top to bottom. (Per-item triggers made them pop in one by one at uneven moments.)
    <motion.ul key={mobile ? 'm' : 'd'} className="flex w-full flex-col items-stretch text-left md:w-fit" {...mobile && !reduce ? {
      variants: GROUP,
      initial: 'hidden',
      whileInView: 'shown',
      viewport: {
        once: true,
        amount: 0.25,
        margin: '0px 0px -10% 0px'
      }
    } : {}} data-id="element-273" data-mp="d4mzvvvyxxsd">
      {services.map((s, i) => {
        const isOpen = open === i;
        const panelId = `service-panel-${i}`;
        return <motion.li key={s.title} className="flex flex-col items-start" {...mobile ? reduce ? {} : {
          variants: ITEM
        } : focusInView(i * 0.06, reduce, {
          amount: 0.8
        })} data-id="element-274" data-mp="3qeavf0uyn026" data-mpi={i}>
            <button type="button" aria-expanded={isOpen} aria-controls={panelId} onClick={() => toggle(i)}
          // Same hover as the site's other text links: a quick fade to half opacity (mouse only)
          className="group flex items-center gap-[0.25em] py-[clamp(0.2rem,calc(0.38*var(--vw)),0.4rem)] text-left transition-opacity duration-200 ease-out [@media(hover:hover)]:hover:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-current" data-id="element-275" data-mp="3ueb92ja8j786">
              <span data-ink="" className="text-[clamp(1.5rem,calc(2.1*var(--vw)),2.25rem)] font-semibold leading-[1.1] tracking-[-0.035em] text-black" data-id="element-276" data-mp="1d5qg7ii6njqc">
                {s.title}
              </span>
              <span data-ink="" className="text-black" data-id="element-277" data-mp="25t44ytbq98zd">
                <ChevronDownIcon aria-hidden="true" strokeWidth={3} className="h-[clamp(1.1rem,calc(1.5*var(--vw)),1.6rem)] w-[clamp(1.1rem,calc(1.5*var(--vw)),1.6rem)] opacity-70 transition-[transform,opacity] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:opacity-100" style={{
                transform: isOpen ? 'rotate(180deg)' : 'none',
                opacity: isOpen ? 1 : undefined
              }} data-id="element-278" data-mp="1r5st0qbmvmyq" />
              </span>
            </button>

            <AnimatePresence initial={false} data-id="element-279" data-mp="vxqwky1766xj">
              {isOpen && <motion.div id={panelId} role="region" aria-label={s.title}
            // w-0 + min-w-full: fills the block without widening it, so the titles never shift
            className="w-0 min-w-full overflow-hidden" initial={reduce ? false : {
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={reduce ? {
              opacity: 0,
              transition: {
                duration: 0
              }
            } : {
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: EASE
            }} data-id="element-280" data-mp="vzv5judydw4o">
                  <motion.div initial={reduce ? false : {
                y: 6,
                filter: 'blur(6px)'
              }} animate={{
                y: 0,
                filter: 'blur(0px)',
                transitionEnd: {
                  filter: 'none'
                }
              }} transition={{
                duration: 0.3,
                ease: EASE
              }} className="text-left pb-[clamp(1rem,calc(1.6*var(--vw)),1.6rem)] pt-[clamp(0.25rem,calc(0.5*var(--vw)),0.5rem)]" data-id="element-281" data-mp="22dkr3hf128b6">
                    {/* Always white: this sits on the blue backdrop, and it mounts on click — after the
                        [data-ink] colour switch has already run — so it would otherwise stay black */}
                    <p className="text-[clamp(1rem,calc(1.2*var(--vw)),1.3rem)] font-semibold leading-[1.4] tracking-[-0.02em] text-white" data-id="element-282" data-mp="2nxmvdwhqz43v">
                      {s.text}
                    </p>
                    {/* Quest: shard two naps inside "Virtual Worlds" */}
                    {s.title === 'Virtual Worlds' && questStep === 'service' && <div className="mt-4 flex items-center" data-id="element-283" data-mp="37ku9dt6za7hf">
                        <QuestShard onCollect={() => quest.found('service')} data-id="element-284" data-mp="37mox1f9mwdp7" />
                      </div>}
                  </motion.div>
                </motion.div>}
            </AnimatePresence>
          </motion.li>;
      })}
    </motion.ul>
  );
}